import { ChangeDetectionStrategy, Component, computed, inject, input } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";

import { AvatarService } from "@bitwarden/common/auth/abstractions/avatar.service";
import { AvatarSize } from "@bitwarden/components";

import { SharedModule } from "../shared";

const sizeClasses: Record<AvatarSize, string> = {
  "2xl": "tw-size-16 tw-min-w-16",
  xl: "tw-size-14 tw-min-w-14",
  lg: "tw-size-11 tw-min-w-11",
  base: "tw-size-8 tw-min-w-8",
  sm: "tw-size-6 tw-min-w-6",
};

@Component({
  selector: "dynamic-avatar",
  imports: [SharedModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <span [title]="title()" [class]="sizeClass()">
      @if (displayImageUrl()) {
        <img
          [src]="displayImageUrl()"
          alt=""
          class="tw-block tw-size-full tw-min-w-full tw-rounded-full tw-object-cover"
        />
      } @else {
        <bit-avatar
          appStopClick
          [text]="text()"
          [size]="size()"
          [color]="color$ | async"
          [id]="id()"
          [title]="title()"
        >
        </bit-avatar>
      }
    </span>
  `,
})
export class DynamicAvatarComponent {
  readonly id = input<string>();
  readonly text = input<string>();
  readonly title = input<string>();
  readonly size = input<AvatarSize>("base");
  /** When set, overrides the synced profile photo URL from {@link AvatarService}. */
  readonly imageUrl = input<string | null | undefined>(undefined);

  private readonly avatarService = inject(AvatarService);

  protected readonly color$ = this.avatarService.avatarColor$;
  private readonly syncedImageUrl = toSignal(this.avatarService.avatarImageUrl$, {
    initialValue: null as string | null,
  });

  protected readonly displayImageUrl = computed(() => {
    const override = this.imageUrl();
    if (override !== undefined) {
      return override;
    }

    return this.syncedImageUrl();
  });

  protected readonly sizeClass = computed(
    () => `tw-inline-block tw-leading-[0px] ${sizeClasses[this.size()]}`,
  );
}
